import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { SectionTitle, StatusChip, Loading } from "@/components/common";
import { ServiceBuilder, ServiceDraftList } from "@/components/ServiceBuilder";
import { Plus, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";

const STAGE_LABEL = {
  arrival: "Arrival", property_scan: "Property Scan", estimate: "Estimate",
  customer_authorization: "Authorization", payment: "Payment", prep: "Prep",
  service: "Service", mid_job_qc: "Mid-Job QC", final_qc: "Final QC",
  before_after_photos: "Photos", customer_walkthrough: "Walkthrough", closeout: "Closeout", closed: "Closed",
};

export default function Jobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [showAllJobs, setShowAllJobs] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [crews, setCrews] = useState([]);
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ customer_id: "", property_id: "", crew_id: "", title: "", same_day: true, is_test: false });

  const load = async () => {
    const [j, c, cr, ft, cat] = await Promise.all([api.get("/jobs"), api.get("/customers"), api.get("/crews"), api.get("/settings/field-test"), api.get("/pricing/catalog")]);
    setJobs(j.data); setCustomers(c.data); setCrews(cr.data); setCatalog(cat.data);
    setForm((f) => ({ ...f, crew_id: cr.data[0]?.id || "", is_test: !!ft.data.field_test_mode }));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onCustomer = async (cid) => {
    setForm((f) => ({ ...f, customer_id: cid, property_id: "" }));
    const { data } = await api.get("/properties", { params: { customer_id: cid } });
    setProperties(data);
    if (data[0]) setForm((f) => ({ ...f, property_id: data[0].id }));
  };

  const create = async () => {
    if (!form.customer_id || !form.property_id || !form.crew_id) { toast.error("Select customer, property and crew"); return; }
    const payload = { ...form, is_test: form.is_test };
    const { data } = await api.post("/jobs", payload);
    if (services.length > 0) {
      await api.post(`/jobs/${data.id}/services`, { services });
    }
    toast.success(`Job created${services.length ? ` with ${services.length} service(s)` : ""}`);
    setOpen(false); setServices([]);
    navigate(`/jobs/${data.id}`);
  };

  if (!jobs) return <Loading label="Loading jobs" />;

  // Closed jobs sink to the bottom; everything else newest-first so a freshly
  // created job (test or live) always surfaces at the top of the Jobs window.
  const sortedJobs = [...jobs].sort((a, b) =>
    ((a.stage === "closed" ? 1 : 0) - (b.stage === "closed" ? 1 : 0)) ||
    (new Date(b.created_at || 0) - new Date(a.created_at || 0)));
  const shownJobs = showAllJobs ? sortedJobs : sortedJobs.slice(0, 6);

  return (
    <div className="max-w-6xl">
      <SectionTitle sub="All jobs assigned to your crew(s)" right={
        <button className="sg-btn sg-btn-accent" onClick={() => setOpen(true)} data-testid="new-job-btn"><Plus size={18} /> New Job</button>
      }>Jobs</SectionTitle>

      <div className="flex items-center justify-between flex-wrap gap-2 mb-3" data-testid="jobs-controls">
        <div className="text-sm" style={{ color: "var(--txt2)" }}>Showing {Math.min(shownJobs.length, jobs.length)} of {jobs.length} · active first</div>
        {jobs.length > 6 && (
          <button className="sg-btn" style={{ minHeight: 38 }} onClick={() => setShowAllJobs((v) => !v)} data-testid="jobs-toggle">
            {showAllJobs ? "Collapse" : `View all ${jobs.length}`}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jobs-list"
        style={showAllJobs ? { maxHeight: 560, overflowY: "auto" } : undefined}>
        {jobs.length === 0 && <div className="text-sm" style={{ color: "var(--txt2)" }}>No jobs yet.</div>}
        {shownJobs.map((j) => (
          <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`jobcard-${j.id}`}
            className="sg-card p-5 text-left hover:border-[var(--accent)]" style={{ transition: "border-color .12s" }}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-head text-lg font-bold">{j.title || "Untitled Job"}</span>
              <ChevronRight size={20} style={{ color: "var(--txt2)" }} />
            </div>
            {(j.customer_name || j.address) && <div className="text-xs mb-2" style={{ color: "var(--txt2)" }}>{j.customer_name}{j.address ? ` · ${j.address}` : ""}</div>}
            <div className="flex flex-wrap gap-2">
              {j.is_test && <span className="chip" data-testid={`test-badge-${j.id}`} style={{ background: "rgba(245,158,11,0.18)", color: "#F59E0B", borderColor: "#F59E0B", fontWeight: 700 }}>TEST</span>}
              <StatusChip status={j.stage === "closed" ? "ready" : "in_progress"} label={STAGE_LABEL[j.stage] || j.stage} />
              <StatusChip status={j.payment_state} />
            </div>
            <div className="text-sm mt-3 font-mono" style={{ color: "var(--txt2)" }}>${(j.collected_revenue || 0).toLocaleString()} collected</div>
          </button>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setOpen(false)}>
          <div className="sg-card p-6 w-full max-w-md sg-fade" style={{ maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()} data-testid="new-job-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head text-2xl font-bold">New Job</h3>
              <button onClick={() => setOpen(false)}><X size={22} /></button>
            </div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Customer</label>
            <select className="sg-input mt-1 mb-3" value={form.customer_id} onChange={(e) => onCustomer(e.target.value)} data-testid="job-customer">
              <option value="">Select customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Property</label>
            <select className="sg-input mt-1 mb-3" value={form.property_id} onChange={(e) => setForm((f) => ({ ...f, property_id: e.target.value }))} data-testid="job-property">
              <option value="">Select property…</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.address}</option>)}
            </select>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Crew</label>
            <select className="sg-input mt-1 mb-3" value={form.crew_id} onChange={(e) => setForm((f) => ({ ...f, crew_id: e.target.value }))} data-testid="job-crew">
              {crews.map((c) => <option key={c.id} value={c.id}>{c.crew_number}</option>)}
            </select>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Title (optional label)</label>
            <input className="sg-input mt-1 mb-4" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="job-title" placeholder="e.g. Smith — Porch + Fleet" />

            <div className="border-t pt-3 mb-3" style={{ borderColor: "var(--brd)" }}>
              <div className="font-head text-sm font-bold mb-1">Services</div>
              <p className="text-xs mb-3" style={{ color: "var(--txt2)" }}>Job scope comes from structured services (not the title). Add each service; use Quantity for multiple vehicles.</p>
              {catalog && <ServiceBuilder catalog={catalog} onAdd={(s) => setServices((prev) => [...prev, s])} />}
              <div className="mt-3"><ServiceDraftList services={services} onRemove={(i) => setServices((prev) => prev.filter((_, idx) => idx !== i))} /></div>
            </div>

            <div className="text-xs mb-3 p-2 rounded-lg" style={{ background: "rgba(59,130,246,0.08)", color: "var(--txt2)" }}>Pricing is set later via Shadow Scan → Estimator (a tier is chosen per service). No manual total here.</div>
            <label className="flex items-center gap-2 text-sm mb-3">
              <input type="checkbox" checked={form.same_day} onChange={(e) => setForm((f) => ({ ...f, same_day: e.target.checked }))} data-testid="job-sameday" /> Same-day immediate job
            </label>
            <label className="flex items-center gap-2 text-sm mb-4" style={{ color: form.is_test ? "#F59E0B" : "var(--txt)" }}>
              <input type="checkbox" checked={form.is_test} onChange={(e) => setForm((f) => ({ ...f, is_test: e.target.checked }))} data-testid="job-is-test" /> Controlled Field Test job (excluded from live metrics)
            </label>
            <button className="sg-btn sg-btn-accent w-full" onClick={create} data-testid="create-job-submit">Create Job</button>
          </div>
        </div>
      )}
    </div>
  );
}
